import { useState, useEffect, useCallback } from 'react';
import api from './useApi';

// Per-person unified time-logging favourites, covering the three things you log
// against: a task, a project (SOW) or an overhead category. Shared by Quick Time
// Log and My Time so the star toggles and the Favourites quick-pick agree.
export default function useTimeFavourites(userId) {
  const [favourites, setFavourites] = useState([]); // [{ kind, ref_id }]

  const load = useCallback(() => {
    if (!userId) { setFavourites([]); return; }
    api.get('time-favourites')
      .then(d => setFavourites(Array.isArray(d) ? d.map(f => ({ kind: f.kind, ref_id: String(f.ref_id), label: f.label || '' })) : []))
      .catch(() => setFavourites([]));
  }, [userId]);

  useEffect(load, [load]);

  const has = useCallback(
    (kind, refId) => favourites.some(f => f.kind === kind && f.ref_id === String(refId)),
    [favourites]
  );

  const toggle = useCallback((kind, refId, label) => {
    const id = String(refId);
    const isFav = favourites.some(f => f.kind === kind && f.ref_id === id);
    setFavourites(prev => isFav ? prev.filter(f => !(f.kind === kind && f.ref_id === id)) : [...prev, { kind, ref_id: id, label: label || '' }]); // optimistic
    const req = isFav ? api.delete(`time-favourites/${kind}/${id}`) : api.post('time-favourites', { kind, ref_id: id, label: label || null });
    req.catch(load); // reconcile if the write failed
  }, [favourites, load]);

  // SOW-only view, so SowSelect (which expects an array of sow_id strings) can
  // use this same store without changing its API.
  const sowFavourites = favourites.filter(f => f.kind === 'sow').map(f => f.ref_id);

  return { favourites, sowFavourites, has, toggle, reload: load };
}
